import { AssetInfo } from '@ergolabs/ergo-sdk';
import {
  Animation,
  Box,
  Flex,
  Form,
  Typography,
  useDevice,
  useFormContext,
} from '@ergolabs/ui-kit';
import React, { FC, ReactNode } from 'react';
import { Observable, of } from 'rxjs';

import { panalytics } from '../../../common/analytics';
import { PAnalytics } from '../../../common/analytics/@types/types';
import { useObservable } from '../../../common/hooks/useObservable';
import { Currency } from '../../../common/models/Currency';
import { useAssetsBalance } from '../../../gateway/api/assetBalance';
import { ConvenientAssetView } from '../../ConvenientAssetView/ConvenientAssetView';
import {
  AssetAmountInput,
  TokenAmountInputValue,
} from './AssetAmountInput/AssetAmountInput';
import { AssetBalance } from './AssetBalance/AssetBalance';
import { AssetSelect } from './AssetSelect/AssetSelect';
export interface TokenControlValue {
  amount?: TokenAmountInputValue;
  asset?: AssetInfo;
}

export interface TokenControlProps {
  readonly label?: ReactNode;
  readonly value?: TokenControlValue;
  readonly onChange?: (value: TokenControlValue) => void;
  readonly maxButton?: boolean;
  readonly hasBorder?: boolean;
  readonly assets?: AssetInfo[];
  readonly disabled?: boolean;
  readonly readonly?: boolean | 'asset' | 'amount';
  readonly noBottomInfo?: boolean;
  readonly bordered?: boolean;
}

export interface TokenControlFormItemProps {
  readonly name: string;
  readonly label?: ReactNode;
  readonly maxButton?: boolean;
  readonly hasBorder?: boolean;
  readonly assets?: AssetInfo[];
  readonly disabled?: boolean;
  readonly readonly?: boolean | 'asset' | 'amount';
  readonly noBottomInfo?: boolean;
  readonly bordered?: boolean;
}

export interface AssetControlFormItemProps {
  readonly amountName?: string;
  readonly tokenName?: string;
  readonly maxButton?: boolean;
  readonly handleMaxButtonClick?: (balance: Currency) => Currency;
  readonly hasBorder?: boolean;
  readonly assets$?: Observable<AssetInfo[]>;
  readonly assetsToImport$?: Observable<AssetInfo[]>;
  readonly importedAssets$?: Observable<AssetInfo[]>;
  readonly disabled?: boolean;
  readonly readonly?: boolean | 'asset' | 'amount';
  readonly noBottomInfo?: boolean;
  readonly bordered?: boolean;
  readonly analytics?: PAnalytics;
  readonly loading?: boolean;
  readonly label: string;
}

export const AssetControlFormItem: FC<AssetControlFormItemProps> = ({
  amountName,
  tokenName,
  maxButton,
  assets$,
  assetsToImport$,
  importedAssets$,
  disabled,
  readonly,
  handleMaxButtonClick,
  analytics,
  loading,
  label,
}) => {
  const { valBySize } = useDevice();
  const { form } = useFormContext();
  const [balance, balanceLoading] = useAssetsBalance();
  const [selectedAsset] = useObservable(
    tokenName
      ? form.controls[tokenName].valueChangesWithSilent$
      : of(undefined),
  );
  const _handleMaxButtonClick = (maxBalance: Currency) => {
    if (amountName) {
      const newAmount = handleMaxButtonClick
        ? handleMaxButtonClick(maxBalance)
        : maxBalance;

      form.controls[amountName].patchValue(
        newAmount.isPositive() ? newAmount : maxBalance,
      );
    }
  };

  const isAmountReadOnly = () => {
    if (typeof readonly === 'boolean') {
      return readonly;
    }

    return readonly === 'amount';
  };

  const isAssetReadOnly = () => {
    if (typeof readonly === 'boolean') {
      return readonly;
    }

    return readonly === 'asset';
  };

  return (
    <div className="mt-6">
      <div className="flex flex-col justify-center content-center">
          <label>{label}</label>
        <div className="flex justify-center content-center relative">
          <div className="flex w-full">
            {amountName && (
              <Form.Item name={amountName}>
                {({ value, onChange }: { value: any; onChange: any }) => (
                  <AssetAmountInput
                    readonly={isAmountReadOnly() || loading}
                    value={value}
                    asset={selectedAsset}
                    onChange={onChange}
                    disabled={disabled}
                    label={label}
                  />
                )}
              </Form.Item>
            )}
          </div>
          <div className="absolute right-2 top-[calc(50%-36px/2)]">
            {tokenName && (
              <Form.Item name={tokenName}>
                {({ value, onChange }: { value: any; onChange: any }) => (
                  <AssetSelect
                    loading={loading}
                    assets$={assets$}
                    assetsToImport$={assetsToImport$}
                    importedAssets$={importedAssets$}
                    readonly={isAssetReadOnly()}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    analytics={analytics}
                  />
                )}
              </Form.Item>
            )}
          </div>
        </div>
         <Form.Listener name={amountName}>
          {({ value }) => (
            <Animation.Expand
              expanded={
                (selectedAsset !== undefined &&
                  !balanceLoading &&
                  readonly !== true) ||
                value?.isPositive()
              }
            >
              <Flex.Item align="center" marginTop={2}>
                <Flex.Item flex={1}>
                  <Typography.Body secondary size={valBySize('small', 'base')}>
                    <ConvenientAssetView
                      value={value}
                      defaultValue="~$0.00"
                      type={'default'}
                    />
                  </Typography.Body>
                </Flex.Item>
                {selectedAsset !== undefined &&
                  !balanceLoading &&
                  readonly !== true && (
                    <AssetBalance
                      balance={balance.get(selectedAsset)}
                      onClick={
                        maxButton
                          ? () =>
                              _handleMaxButtonClick(balance.get(selectedAsset))
                          : undefined
                      }
                    />
                  )}
              </Flex.Item>
            </Animation.Expand>
          )}
        </Form.Listener>
      </div>
    </div>
  );
};
